import React, { useEffect, useState, Suspense } from "react";
import {
  Box,
  Button,
  Input,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  IconButton,
  Stack,
  Spinner,
  useToast,
  Image,
  Skeleton,
  SkeletonText,
  SkeletonCircle,
  ChakraProvider,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import Sidebar from "../Sidebar";
import Navbar from "../Navbar/Navbar";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";
import * as mod from "../../url"; // Update with your actual URL file path

const token = JSON.parse(sessionStorage.getItem("superAdminLawvs"))?.data
  ?.token;
const config = { headers: { Authorization: token } };

const SIDEBAR_WIDTH = "250px";
const RECORDS_PER_PAGE = 20;

const AllBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const toast = useToast();

  // Fetch blogs from API
  const fetchAllBlogs = async () => {
    try {
      const { data } = await axios.get(`${mod.api_url}/api/v1/blogs/get_blogs`);
      setBlogs(data.blogs);
    } catch (error) {
      console.error("Error fetching blogs:", error);
      toast({
        title: "Failed to fetch blogs.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllBlogs();
  }, []);

  const handleStatusToggle = async (slug, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await axios.put(
        `${mod.api_url}/api/v1/blogs/status_blogs/${slug}`,
        { status: newStatus },
        config
      );

      setBlogs((prev) =>
        prev.map((b) => (b.slug === slug ? { ...b, status: newStatus } : b))
      );
    } catch (error) {
      console.error("Failed to update status", error);
    }
  };

  const handleDelete = async (slug) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this blog?"
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(
        `${mod.api_url}/api/v1/blogs/delete_blogs/${slug}`,
        config
      );
      setBlogs((prev) => prev.filter((b) => b.slug !== slug));
      toast({
        title: "Blog deleted successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Failed to delete blog.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // ✅ Filter logic
  const filteredBlogs = blogs.filter((b) =>
    b?.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ✅ Pagination
  const totalPages = Math.ceil(filteredBlogs.length / RECORDS_PER_PAGE);
  const startIdx = (currentPage - 1) * RECORDS_PER_PAGE;
  const currentRecords = filteredBlogs.slice(
    startIdx,
    startIdx + RECORDS_PER_PAGE
  );

  return (
    <>
      <Navbar />
      <Sidebar />
      <Box mt="70px" ml={{ base: 0, md: SIDEBAR_WIDTH }} p={3}>
        <Text fontSize="2xl" mb={4} bg="#D29B3F" p={3}>
          <strong>All Blogs</strong>
        </Text>

        {/* Search + Add New */}
        <Flex
          bg="white"
          p={4}
          mb={4}
          borderRadius="md"
          boxShadow="sm"
          align="center"
          flexWrap="wrap"
          gap={4}
        >
          <Input
            placeholder="Search by title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            maxW="300px"
          />
          <Flex ml="auto">
            <Button
              colorScheme="blue"
              leftIcon={<AddIcon />}
              as={RouterLink}
              to="/create-blog"
              _hover={{ bg: "blue.100" }}
            >
              Add New Record
            </Button>
          </Flex>
        </Flex>

        <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
          <Text fontSize="sm" mb={2} textAlign="right">
            Total Records: {filteredBlogs.length}
          </Text>

          {/* ✅ Suspense with Skeleton */}
          <Suspense
            fallback={
              <Stack>
                {[...Array(5)].map((_, i) => (
                  <Box key={i} p={3} borderWidth="1px" borderRadius="md">
                    <Skeleton height="20px" width="30%" mb={2} />
                    <SkeletonText noOfLines={2} spacing="2" />
                  </Box>
                ))}
              </Stack>
            }
          >
            {loading ? (
              <Flex justify="center" align="center" py={10}>
                <Spinner size="xl" />
              </Flex>
            ) : (
              <>
                <Table
                  variant="simple"
                  size="sm"
                  style={{ border: "1px solid #ccc" }}
                >
                  <Thead bg="gray.100">
                    <Tr>
                      <Th border="1px solid #ccc">ID</Th>
                      <Th border="1px solid #ccc">Image</Th>
                      <Th border="1px solid #ccc">Title</Th>
                      <Th border="1px solid #ccc">Posted On</Th>
                      <Th border="1px solid #ccc">Action</Th>
                      <Th border="1px solid #ccc">Status</Th>
                      <Th border="1px solid #ccc">Delete</Th>
                    </Tr>
                  </Thead>

                  <Tbody>
                    {currentRecords.map((b, index) => (
                      <Tr key={b._id}>
                        <Td border="1px solid #ccc">{startIdx + index + 1}</Td>
                        <Td border="1px solid #ccc">
                          <Image
                            src={b?.blogImage}
                            alt={b?.title}
                            width="120px"
                            height="80px"
                            // objectFit="cover"
                          />
                        </Td>
                        <Td border="1px solid #ccc">
                          <ChakraLink
                            // as={RouterLink}
                            // to={`/update-blogs/${b.slug}`}
                            color="blue.500"
                            _hover={{ textDecoration: "none" }}
                          >
                            {b?.title?.slice(0, 80)}
                          </ChakraLink>
                        </Td>
                        <Td border="1px solid #ccc">
                          {new Date(b?.postedOn).toLocaleDateString()}
                        </Td>
                        <Td border="1px solid #ccc">
                          <ChakraLink
                            as={RouterLink}
                            to={`/update-blogs/${b?.slug}`}
                          >
                            <IconButton
                              icon={<EditIcon />}
                              colorScheme="blue"
                              size="sm"
                            />
                          </ChakraLink>
                        </Td>
                        <Td border="1px solid #ccc">
                          <Button
                            size="sm"
                            colorScheme={
                              b?.status === "active" ? "green" : "red"
                            }
                            onClick={() => handleStatusToggle(b.slug, b.status)}
                          >
                            {b?.status === "active" ? "Active" : "Inactive"}
                          </Button>
                        </Td>
                        <Td border="1px solid #ccc">
                          <IconButton
                            icon={<DeleteIcon />}
                            colorScheme="red"
                            size="sm"
                            onClick={() => handleDelete(b.slug)}
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>

                {/* Pagination */}
                <Flex justify="space-between" align="center" mt={4}>
                  <Stack direction="row" spacing={2}>
                    {Array.from({ length: totalPages }).map((_, idx) => (
                      <Button
                        key={idx}
                        size="sm"
                        onClick={() => setCurrentPage(idx + 1)}
                        colorScheme={currentPage === idx + 1 ? "blue" : "gray"}
                        variant={currentPage === idx + 1 ? "solid" : "outline"}
                      >
                        {idx + 1}
                      </Button>
                    ))}
                  </Stack>
                </Flex>
              </>
            )}
          </Suspense>
        </Box>
      </Box>
    </>
  );
};

export default AllBlogs;
